import { supabase } from './supabase';
import { CouponValidationResult, UserCouponStats, AppliedCoupon } from '../types/coupon';

/**
 * Validates a coupon code and returns discount details
 * Uses the validate_coupon database function for atomic validation
 */
export async function validateCoupon(
  code: string,
  userId: string,
  venueId: string,
  subtotal: number
): Promise<CouponValidationResult> {
  try {
    const { data, error } = await supabase.rpc('validate_coupon', {
      p_code: code.trim().toUpperCase(),
      p_user_id: userId,
      p_venue_id: venueId,
      p_subtotal: subtotal,
    });

    if (error) {
      console.error('Error validating coupon:', error);
      return {
        valid: false,
        error: 'Unable to validate coupon. Please try again.',
      };
    }

    return data as CouponValidationResult;
  } catch (err) {
    console.error('Exception validating coupon:', err);
    return {
      valid: false,
      error: 'An unexpected error occurred.',
    };
  }
}

/**
 * Gets user's usage statistics for a specific coupon
 */
export async function getUserCouponStats(
  userId: string,
  couponId: string
): Promise<UserCouponStats | null> {
  try {
    const { data, error } = await supabase.rpc('get_user_coupon_stats', {
      p_user_id: userId,
      p_coupon_id: couponId,
    });

    if (error) {
      console.error('Error getting user coupon stats:', error);
      return null;
    }

    return data?.[0] || null;
  } catch (err) {
    console.error('Exception getting user coupon stats:', err);
    return null;
  }
}

/**
 * Records coupon usage after successful payment
 * This is typically called automatically by the database trigger,
 * but can be called manually if needed
 */
export async function recordCouponUsage(
  couponId: string,
  userId: string,
  orderId: string,
  discountAmount: number
): Promise<boolean> {
  try {
    const { error } = await supabase.rpc('record_coupon_usage', {
      p_coupon_id: couponId,
      p_user_id: userId,
      p_order_id: orderId,
      p_discount_amount: discountAmount,
    });

    if (error) {
      console.error('Error recording coupon usage:', error);
      return false;
    }

    return true;
  } catch (err) {
    console.error('Exception recording coupon usage:', err);
    return false;
  }
}

/**
 * Fetches active coupons (optionally filtered by venue)
 * Used for displaying available coupons to users
 */
export async function getActiveCoupons(venueId?: string) {
  try {
    let query = supabase
      .from('coupons')
      .select('*')
      .eq('is_active', true)
      .gte('valid_until', new Date().toISOString())
      .lte('valid_from', new Date().toISOString())
      .order('created_at', { ascending: false });

    // If venue_id is provided, filter for applicable coupons
    if (venueId) {
      query = query.or(`applicable_venue_ids.is.null,applicable_venue_ids.cs.{${venueId}}`);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching active coupons:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Exception fetching active coupons:', err);
    return [];
  }
}

/**
 * Gets user's coupon usage history
 */
export async function getUserCouponHistory(userId: string) {
  try {
    const { data, error } = await supabase
      .from('coupon_usage')
      .select(`
        *,
        coupons (
          code,
          description,
          discount_type,
          discount_value
        ),
        orders (
          id,
          created_at,
          total_amount,
          status
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching user coupon history:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Exception fetching user coupon history:', err);
    return [];
  }
}

/**
 * Converts validation result to applied coupon
 */
export function validationResultToAppliedCoupon(
  result: CouponValidationResult
): AppliedCoupon | null {
  if (!result.valid || !result.coupon_id || !result.code) {
    return null;
  }

  return {
    id: result.coupon_id,
    code: result.code,
    description: result.description || null,
    discount_type: result.discount_type!,
    discount_value: result.discount_value!,
    discount_amount: result.discount_amount!,
    funded_by: result.funded_by,
  };
}

/**
 * Calculates discount amount based on coupon type
 * This is a client-side helper for preview purposes
 * The actual discount is calculated by the database function
 */
export function calculateDiscount(
  discountType: 'percentage' | 'fixed_amount',
  discountValue: number,
  subtotal: number
): number {
  if (discountType === 'percentage') {
    return Math.round((subtotal * discountValue / 100) * 100) / 100;
  } else {
    // Fixed amount - can't be more than subtotal
    return Math.min(discountValue, subtotal);
  }
}

/**
 * Formats discount for display
 */
export function formatDiscountText(
  discountType: 'percentage' | 'fixed_amount',
  discountValue: number
): string {
  if (discountType === 'percentage') {
    return `${discountValue}% off`;
  } else {
    return `$${discountValue.toFixed(2)} off`;
  }
}
